import {
  BRIDGE_SEATS,
  type BridgeCard,
  type BridgeGame,
  type BridgeResult,
  type BridgeSeat,
  type BridgeTrump,
  SINGLE_PLAYER,
  actingSeat,
  bridgeResult,
  chooseTrump,
  newBridgeGame,
  playCard,
  robotChooseCard,
  robotChooseTrump,
  seededRandom,
  viewFor,
} from '@chessmate/game-engine';
import { create } from 'zustand';
import type { AppQuery } from '../lib/query.js';
import { playSfx, setAmbientMood } from '../services/audio.js';
import { cueCardPlay, cueDeal, cueRoundOrGame, cueSweep, cueTurn } from './sfx-cues.js';
import { CHAT_BRIDGE, SIDE_SEATS, chatRound, chatTrick, chatterNewGame, robotsOf } from './table-chatter.js';

/**
 * The Mini-Bridge store (single device: you in South, three robots). Its own store, like backgammon's,
 * because nothing here is FEN-shaped. The seat you play is `HUMAN_SEAT`; every robot decides from
 * `viewFor(game, seat)` only (docs/proposals/mindgames-and-bridge.md), the same pure functions a
 * server would use for AI seats at a multiplayer table.
 *
 * The screen renders from `viewFor(game, HUMAN_SEAT)` as well, so a leak into the UI would also be a
 * leak the engine's privacy tests already forbid.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';

/** Robots in the other seats: a gentle partner and opponents for a first game. */
export type BridgeStrength = 'easy' | 'clever';

/** Pause before a robot plays, so a kid can follow the trick. Zero in tests and `?engine=fake`. */
const ROBOT_DELAY_MS = 650;

export interface BridgeState {
  game: BridgeGame | null;
  result: BridgeResult | null;
  strength: BridgeStrength;
  /** Assist toggles (all off by default so the first look is the plain game). */
  showPoints: boolean;
  fourColour: boolean;
  /** The seed of the current deal, shown so a deal can be replayed. */
  seed: number | null;
  query: AppQuery | null;
  start(query: AppQuery, seed?: number): void;
  redeal(): void;
  stop(): void;
  pickTrump(trump: BridgeTrump): void;
  play(card: BridgeCard): void;
  setStrength(strength: BridgeStrength): void;
  setShowPoints(on: boolean): void;
  setFourColour(on: boolean): void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);

export const useBridgeStore = create<BridgeState>((set, get) => {
  const finish = (game: BridgeGame) => {
    const before = get().game;
    const result = bridgeResult(game);
    set({ game, result });
    // A card reaching the table is heard from the seat whose hand it came out of (spec P2: seat panning).
    // That is `turn`, not `actingSeat`: the declarer plays the dummy's cards, and those are heard from the dummy's side.
    if (before?.phase === 'play') {
      cueCardPlay(before.turn);
      cueSweep(before, game);
      chatTrick(before, game, robotsOf(BRIDGE_SEATS), CHAT_BRIDGE);
    }
    if (before && before.phase === 'trump') playSfx('trump-chosen');
    if (result) {
      // The deal is the round: made contract = won; the human's side defending a failed one = won too.
      const humanDeclaring = result.declaringSide === 'NS';
      cueRoundOrGame(result.made === humanDeclaring ? 'won' : 'lost', false);
      // The contract made or failed is public: the robot on the side that came out ahead is pleased, the other kind in defeat.
      const robots = robotsOf(BRIDGE_SEATS);
      const winSide = result.made ? result.declaringSide : result.declaringSide === 'NS' ? 'EW' : 'NS';
      const loseSide = winSide === 'NS' ? 'EW' : 'NS';
      chatRound(robots.find((r) => SIDE_SEATS[winSide].includes(r)) ?? null, robots.find((r) => SIDE_SEATS[loseSide].includes(r)) ?? null, CHAT_BRIDGE);
    } else {
      cueTurn(before ? actingSeat(before) : null, actingSeat(game), HUMAN_SEAT);
    }
  };

  /** If a robot is to act, let it, after a short pause; stops at the human's turn or the end. */
  const driveRobots = () => {
    clearTimer();
    const { game, query, strength } = get();
    if (!game || game.phase === 'done') return;
    const actor = actingSeat(game);
    if (!actor || game.controllers[actor] !== 'ai') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    timer = setTimeout(() => {
      const current = get().game;
      if (!current || current.phase === 'done') return;
      const seat = actingSeat(current);
      if (!seat || current.controllers[seat] !== 'ai') return;
      const view = viewFor(current, seat);
      const next =
        current.phase === 'trump'
          ? chooseTrump(current, robotChooseTrump(view))
          : playCard(
              current,
              robotChooseCard(view, strength, seededRandom(current.seed + current.tricks.length * 4 + current.trick.length)),
            );
      finish(next);
      driveRobots();
    }, delay);
  };

  const deal = (query: AppQuery, seed: number) => {
    clearTimer();
    chatterNewGame();
    const game = newBridgeGame({ seed, dealer: 'N', controllers: SINGLE_PLAYER });
    set({ game, result: null, seed, query });
    cueDeal();
    cueTurn(null, actingSeat(game), HUMAN_SEAT);
    driveRobots();
  };

  return {
    game: null,
    result: null,
    strength: 'easy',
    showPoints: false,
    fourColour: false,
    seed: null,
    query: null,
    start: (query, seed) => {
      setAmbientMood('cards');
      return deal(query, seed ?? query.deal ?? freshSeed());
    },
    redeal: () => {
      const { query } = get();
      if (query) deal(query, freshSeed());
    },
    stop: () => {
      clearTimer();
      set({ game: null, result: null, seed: null });
    },
    pickTrump: (trump) => {
      const { game } = get();
      if (!game || game.phase !== 'trump' || game.contract.declarer !== HUMAN_SEAT) return;
      finish(chooseTrump(game, trump));
      driveRobots();
    },
    play: (card) => {
      const { game } = get();
      if (!game || game.phase !== 'play' || actingSeat(game) !== HUMAN_SEAT) return;
      const legal = viewFor(game, HUMAN_SEAT).legalPlays;
      if (!legal.some((c) => c.suit === card.suit && c.rank === card.rank)) return;
      // Only a card that reaches the table is heard (in `finish`); an illegal tap is silent.
      finish(playCard(game, card));
      driveRobots();
    },
    setStrength: (strength) => set({ strength }),
    setShowPoints: (showPoints) => set({ showPoints }),
    setFourColour: (fourColour) => set({ fourColour }),
  };
});
